/**
 * File validation utilities
 */

// File size constants
export const FILE_SIZE_LIMITS = {
    MAX_SIZE_MB: 10,
    MAX_SIZE_BYTES: 10485760, // 10MB in bytes
};

// Allowed file types
export const ALLOWED_FILE_TYPES = {
    DOCUMENTS: ['.pdf', '.jpg', '.jpeg', '.png'],
    MIME_TYPES: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
};

/**
 * Validate file size
 * @param {File} file - The file to validate
 * @param {number} maxSizeBytes - Maximum file size in bytes (default: 10MB)
 * @returns {boolean|string} - Returns true if valid, error message if invalid
 */
export const validateFileSize = (file, maxSizeBytes = FILE_SIZE_LIMITS.MAX_SIZE_BYTES) => {
    if (!file) {
        return 'No file selected';
    }

    if (file.size > maxSizeBytes) {
        const maxSizeMB = maxSizeBytes / 1048576;
        return `File size must not exceed ${maxSizeMB}MB`;
    }

    return true;
};

/**
 * Validate file type
 * @param {File} file - The file to validate
 * @param {Array} allowedTypes - Array of allowed file extensions (default: PDF, JPG, PNG)
 * @returns {boolean|string} - Returns true if valid, error message if invalid
 */
export const validateFileType = (file, allowedTypes = ALLOWED_FILE_TYPES.DOCUMENTS) => {
    if (!file) {
        return 'No file selected';
    }

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

    const isValidExtension = allowedTypes.some(type =>
        fileExtension === type.toLowerCase()
    );

    if (!isValidExtension) {
        const allowedTypesStr = allowedTypes.join(', ').toUpperCase();
        return `Invalid file type. Allowed types: ${allowedTypesStr}`;
    }

    return true;
};

/**
 * Validate file (both size and type)
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {Object} - Returns { isValid: boolean, error: string|null }
 */
export const validateFile = (file, options = {}) => {
    const {
        maxSizeBytes = FILE_SIZE_LIMITS.MAX_SIZE_BYTES,
        allowedTypes = ALLOWED_FILE_TYPES.DOCUMENTS,
    } = options;

    // Validate file size
    const sizeValidation = validateFileSize(file, maxSizeBytes);
    if (sizeValidation !== true) {
        return { isValid: false, error: sizeValidation };
    }

    // Validate file type
    const typeValidation = validateFileType(file, allowedTypes);
    if (typeValidation !== true) {
        return { isValid: false, error: typeValidation };
    }

    return { isValid: true, error: null };
};

/**
 * Format file size to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};
